/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import isEqual from 'lodash.isequal';
import {entityId} from '@ion/core/src/services/ConnectUtils';

/**
 * Invoked when an existing entity is to be updated; consolidating the process of
 * Entity update and fetch into a single transaction. The process for editing a entity is
 * outlined below.
 *
 * 1.) Update the given entity.
 *
 * 2.) When the entity returns Resource Out of State error.
 *
 * 3.) Fetch the latest details of the entity.
 *
 * 4.) Update the given entity with latest details fetched from the entity.
 *
 * ```typescript
 *
 *   // passing default params to function ...
 *   const updated = retryUpdate({
 *     newEntity    : entity,
 *     updateEntity : updateEntity,
 *     getEntity    : getSingleEntity,
 *     onError      : entityUpdateError
 *   });
 *
 *   // passing default with optional params to function ...
 *   const updated = retryUpdate({
 *     newEntity          : entity,
 *     updateEntity       : updateEntity,
 *     getEntity          : getSingleEntity,
 *     onError            : entityUpdateError,
 *     isPegBehavior      : true,
 *     isApplication      : true,
 *     isUser             : true,
 *     useEntityFormatter : true,
 *     dispatch,
 *     context
 *   });
 *
 * ```
 *
 * @param   newEntity The Entity details object.
 * @param   updateEntity The entity function which updates an existing
 *          entity within the required Platform/Service/API.
 * @param   getEntity The entity function which fetches an existing
 *          entity within the required Platform/Service/API.
 * @param   onError Invoked when dispatching a successful or error action accordingly.
 * @param   dispatch Dispatches a successful or error action accordingly.
 * @param   context When provided, will be added as a query string parameter to inform
 *          the MicroServices of the context (e.g. '?suite=true')..
 * @param   isApplication When provided, will be added as application key parameter to
 *          fetch latest entity details.
 * @param   isPegBehavior When provided, will be added as entity object parameter to
 *          fetch latest entity details.
 * @param   isUser When provided, update entity will be added with context parameter to
 *          inform the MicroServices.
 * @param   useEntityFormatter When provided, will be formatted as entity specific
 *          error message.
 * @returns entity successful or error response accordingly.
 *
 */
export const retryUpdate = async({
  newEntity,
  updateEntity,
  getEntity,
  onError,
  dispatch = null,
  context = null,
  isApplication = false,
  isPegBehavior = false,
  isUser = false,
  useEntityFormatter = false
}) => {
  try {
    if (isUser) {
      return await updateEntity(newEntity, context);
    } else {
      return await updateEntity(newEntity);
    }
  } catch (err) {
    const isConflict = isEqual(err?.errorCode, 409);
    if (isConflict) {
      const {updated, error} = await getEntity(
        isApplication ? newEntity.applicationKey
          : isPegBehavior ? newEntity
            : entityId(newEntity)
      );
      if (updated) {
        const updatedEntity = {
          ...newEntity,
          version : updated.version
        };
        try {
          if (isUser) {
            return await updateEntity(updatedEntity, context);
          } else {
            return await updateEntity(updatedEntity);
          }
        } catch (error) {
          onError(error, dispatch, useEntityFormatter);
        }
      } else {
        onError(error, dispatch, useEntityFormatter);
      }
    } else {
      onError(err, dispatch, useEntityFormatter);
    }
  }
};
